import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { verifyListOTP } from '../services/authService';
import { useParams, useNavigate } from 'react-router-dom';

const EmailOTPValidation = ({ open, onClose, onValidationSuccess }) => {
  const { listId } = useParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');


  const handleOTPSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    if (!otp || otp.trim().length === 0) {
      setError('Please enter the OTP');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await verifyListOTP(email, otp, listId);
      
      // If response contains user data, store it and log the user in
      if (response && response.status === 'success' && response.data) {
        localStorage.setItem('userInfo', JSON.stringify(response.data));
        setSuccess('Email verified successfully! Logging you in...');
        
        // Redirect to dashboard after a short delay
        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);
      } else {
        // Fallback: Store validation in localStorage to remember for this session
        setSuccess('Email verified successfully!');
        const validationData = {
          email,
          timestamp: Date.now(),
          listId: window.location.pathname.split('/').pop()
        };
        console.log('Storing validation data:', validationData);
        localStorage.setItem('publicViewValidated', JSON.stringify(validationData));
        setTimeout(() => {
          console.log('Calling onValidationSuccess');
          onValidationSuccess();
        }, 1000);
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      setError(error.response?.data?.message || 'Invalid email or OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };


  const handleClose = () => {
    setEmail('');
    setOtp('');
    setError('');
    setSuccess('');
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: 1
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
          Email & OTP Verification
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Box>
          <Typography variant="body2" sx={{ mb: 3, color: '#666' }}>
            Please enter your email address and the verification code to access this public list.
          </Typography>
          
          <form onSubmit={handleOTPSubmit}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              variant="outlined"
              sx={{ mb: 2 }}
              disabled={loading}
              autoFocus
            />
            
            <TextField
              fullWidth
              label="Enter OTP"
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              variant="outlined"
              sx={{ mb: 2 }}
              disabled={loading}
              inputProps={{
                style: { textAlign: 'center', fontSize: '1.2rem', letterSpacing: '0.1rem' }
              }}
            />
            
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}
          </form>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleOTPSubmit}
          variant="contained"
          disabled={loading || !email || !otp || otp.trim().length === 0}
          sx={{
            bgcolor: '#1BE885',
            '&:hover': { bgcolor: '#17d077' },
            px: 4,
            py: 1,
            width: '100%'
          }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : 'Verify'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailOTPValidation;
